import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Plus, Trash2, X } from 'lucide-react';
import { SmallButton } from '../abf/onboarding/review/contractReviewStyles';
import type { CrossFieldCheck, TemplateJson, TemplateProblem } from './documentTemplatesApi';

const Panel = styled.section`
  border: 1px solid #29374b; border-radius: 10px; background: #0c1524; margin-bottom: 16px; overflow: hidden;
  > h3 { padding: 12px 16px; background: #0f1b2b; border-bottom: 1px solid #29374b; font-size: 14px; }
  .check { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; padding: 11px 16px; }
  .check + .check { border-top: 1px solid #1f2b3a; }
  .check[data-problem='true'] { background: #2a1717; }
  .words { color: #e5edf7; font-size: 14px; }
  .term { display: inline-flex; align-items: center; gap: 3px; }
  .term button { background: none; border: 0; color: #91a5bd; cursor: pointer; padding: 0 2px; }
  .term button:hover { color: #fca5a5; }
  small { display: block; color: #91a5bd; font-size: 12px; margin-top: 3px; }
  .problem { color: #fca5a5; font-size: 12px; margin-top: 4px; }
  .empty { padding: 12px 16px; color: #91a5bd; font-size: 13px; }
`;

const Form = styled.form`
  display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px 16px; padding: 16px; background: #101d2e;
  border-top: 1px solid #1f2b3a;
  label { display: flex; flex-direction: column; gap: 5px; color: #b6c7da; font-size: 12px; }
  label.wide { grid-column: 1 / -1; }
  input, select { padding: 8px 10px; min-height: 38px; background: #142033; color: #e5edf7; border: 1px solid #33465e;
    border-radius: 6px; font-size: 13px; }
  .terms { grid-column: 1 / -1; display: flex; flex-wrap: wrap; gap: 6px 16px; color: #c5d4e6; font-size: 13px; }
  .terms label { flex-direction: row; align-items: center; gap: 6px; color: #c5d4e6; font-size: 13px; }
  .buttons { grid-column: 1 / -1; display: flex; gap: 10px; justify-content: flex-end; }
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const NUMERIC = ['money', 'percent', 'bps', 'number', 'integer'];
const RULES: Array<{ value: CrossFieldCheck['rule']; label: string }> = [
  { value: 'SUM_EQUALS', label: 'Values add up to a total' },
  { value: 'LTE', label: 'One value is not more than another' },
  { value: 'DATE_BEFORE', label: 'One date is before another' },
  { value: 'REQUIRED_IF', label: 'A value must be stated when another is' },
];

/** Every value a check may name: a field, or a member of a keyed group, at its fixed path. */
const scalars = (template: TemplateJson) => {
  const out: Array<{ path: string; label: string; type: string }> = [];
  template.fields.forEach(f => out.push({ path: f.path, label: f.label, type: f.type || 'string' }));
  (template.objects || []).forEach(o => (o.item_fields || []).forEach(i =>
    out.push({ path: `${o.path}/${i.name}`, label: `${o.label}: ${i.label}`, type: i.type })));
  return out;
};

const labelOf = (template: TemplateJson, path?: string) =>
  scalars(template).find(s => s.path === path)?.label || path || '?';

interface Props {
  template: TemplateJson;
  editable: boolean;
  problems: TemplateProblem[];
  onChange: (next: TemplateJson) => void;
}

interface Draft {
  rule: CrossFieldCheck['rule'];
  left: string;
  right: string;
  terms: string[];
  total: string;
  tolerance: string;
  field: string;
  when: string;
  equals: string;
  description: string;
}

const EMPTY: Draft = { rule: 'SUM_EQUALS', left: '', right: '', terms: [], total: '', tolerance: '0.01', field: '', when: '', equals: '', description: '' };

/**
 * Checks across fields (F-DT-005): read in words, and on a draft removed, trimmed or added, so a field a
 * check names can be retired from the screen without leaving a problem nobody can fix.
 */
const CrossFieldChecksPanel: React.FC<Props> = ({ template, editable, problems, onChange }) => {
  const checks = template.cross_field_checks || [];
  const [draft, setDraft] = useState<Draft | null>(null);
  const values = useMemo(() => scalars(template), [template]);
  const numeric = values.filter(v => NUMERIC.includes(v.type));
  const dates = values.filter(v => v.type === 'date');

  const setChecks = (next: CrossFieldCheck[]) => onChange({ ...template, cross_field_checks: next });
  const remove = (index: number) => setChecks(checks.filter((_, i) => i !== index));
  const dropTerm = (index: number, term: string) =>
    setChecks(checks.map((c, i) => (i === index ? { ...c, terms: (c.terms || []).filter(t => t !== term) } : c)));

  const words = (check: CrossFieldCheck, index: number): React.ReactNode => {
    switch (check.rule) {
      case 'SUM_EQUALS':
        return (
          <>
            {(check.terms || []).map((term, t) => (
              <React.Fragment key={term}>
                {t > 0 && ' + '}
                <span className="term">
                  {labelOf(template, term)}
                  {editable && (check.terms || []).length > 2 && (
                    <button type="button" aria-label={`Take ${labelOf(template, term)} out of this sum`} onClick={() => dropTerm(index, term)}>
                      <X size={12} />
                    </button>
                  )}
                </span>
              </React.Fragment>
            ))}
            {' = '}{labelOf(template, check.total)}
            {check.tolerance ? ` (within ${check.tolerance})` : ''}
          </>
        );
      case 'LTE':
        return <>{labelOf(template, check.left)} is not more than {labelOf(template, check.right)}</>;
      case 'DATE_BEFORE':
        return <>{labelOf(template, check.left)} is before {labelOf(template, check.right)}</>;
      case 'REQUIRED_IF':
        return (
          <>
            {labelOf(template, check.field)} must be stated when {labelOf(template, check.when)}
            {check.equals !== undefined ? ` is ${String(check.equals)}` : ' is stated'}
          </>
        );
      default:
        return check.rule;
    }
  };

  const add = (event: React.FormEvent) => {
    event.preventDefault();
    if (!draft) return;
    const description = draft.description.trim() || undefined;
    let check: CrossFieldCheck;
    if (draft.rule === 'SUM_EQUALS') {
      check = { rule: 'SUM_EQUALS', terms: draft.terms, total: draft.total, tolerance: Number(draft.tolerance || 0), description };
    } else if (draft.rule === 'REQUIRED_IF') {
      check = { rule: 'REQUIRED_IF', field: draft.field, when: draft.when, description,
        ...(draft.equals.trim() ? { equals: draft.equals.trim() } : {}) };
    } else {
      check = { rule: draft.rule, left: draft.left, right: draft.right, description };
    }
    setChecks([...checks, check]);
    setDraft(null);
  };

  const set = (patch: Partial<Draft>) => setDraft(current => (current ? { ...current, ...patch } : current));
  const options = (list: typeof values) => list.map(v => <option key={v.path} value={v.path}>{v.label}</option>);
  const ready = draft && (draft.rule === 'SUM_EQUALS' ? draft.terms.length >= 2 && draft.total
    : draft.rule === 'REQUIRED_IF' ? draft.field && draft.when : draft.left && draft.right);

  if (!checks.length && !editable) return null;
  return (
    <Panel aria-label="Checks across fields">
      <h3>Checks across fields</h3>
      {!checks.length && <div className="empty">No checks.</div>}
      {checks.map((check, index) => {
        const own = problems.filter(p => p.pointer === `/cross_field_checks/${index}` || p.pointer.startsWith(`/cross_field_checks/${index}/`));
        return (
          <div className="check" key={index} data-problem={own.length > 0}>
            <div>
              <div className="words">{words(check, index)}</div>
              {check.description && <small>{check.description}</small>}
              {own.map(p => <div className="problem" key={p.pointer + p.message}>{p.message}</div>)}
            </div>
            {editable && (
              <SmallButton type="button" aria-label="Remove this check" onClick={() => remove(index)}><Trash2 size={14} /></SmallButton>
            )}
          </div>
        );
      })}
      {editable && !draft && (
        <div className="check">
          <SmallButton type="button" onClick={() => setDraft({ ...EMPTY })}><Plus size={14} /> Add a check</SmallButton>
        </div>
      )}
      {editable && draft && (
        <Form onSubmit={add} aria-label="New check">
          <label>
            Check
            <select value={draft.rule} onChange={e => set({ ...EMPTY, rule: e.target.value as Draft['rule'] })}>
              {RULES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </label>
          {draft.rule === 'SUM_EQUALS' && (
            <>
              <label>Total<select value={draft.total} onChange={e => set({ total: e.target.value })}><option value="">Choose…</option>{options(numeric)}</select></label>
              <label>Tolerance<input type="number" step="any" min="0" value={draft.tolerance} onChange={e => set({ tolerance: e.target.value })} /></label>
              <div className="terms" role="group" aria-label="Values that add up">
                {numeric.filter(v => v.path !== draft.total).map(v => (
                  <label key={v.path}>
                    <input type="checkbox" checked={draft.terms.includes(v.path)}
                      onChange={e => set({ terms: e.target.checked ? [...draft.terms, v.path] : draft.terms.filter(t => t !== v.path) })} />
                    {v.label}
                  </label>
                ))}
              </div>
            </>
          )}
          {(draft.rule === 'LTE' || draft.rule === 'DATE_BEFORE') && (
            <>
              <label>{draft.rule === 'LTE' ? 'Value' : 'Earlier date'}
                <select value={draft.left} onChange={e => set({ left: e.target.value })}><option value="">Choose…</option>{options(draft.rule === 'LTE' ? numeric : dates)}</select>
              </label>
              <label>{draft.rule === 'LTE' ? 'Not more than' : 'Later date'}
                <select value={draft.right} onChange={e => set({ right: e.target.value })}><option value="">Choose…</option>{options(draft.rule === 'LTE' ? numeric : dates)}</select>
              </label>
            </>
          )}
          {draft.rule === 'REQUIRED_IF' && (
            <>
              <label>Must be stated<select value={draft.field} onChange={e => set({ field: e.target.value })}><option value="">Choose…</option>{options(values)}</select></label>
              <label>When<select value={draft.when} onChange={e => set({ when: e.target.value })}><option value="">Choose…</option>{options(values)}</select></label>
              <label>Is (optional)<input value={draft.equals} onChange={e => set({ equals: e.target.value })} placeholder="Any value" /></label>
            </>
          )}
          <label className="wide">Description<input value={draft.description} onChange={e => set({ description: e.target.value })} placeholder="What the check guards against" /></label>
          <div className="buttons">
            <SmallButton type="button" onClick={() => setDraft(null)}><X size={14} /> Cancel</SmallButton>
            <SmallButton type="submit" disabled={!ready} style={{ borderColor: '#67c6f4' }}>Add check</SmallButton>
          </div>
        </Form>
      )}
    </Panel>
  );
};

export default CrossFieldChecksPanel;
